// Lazy-load Firebase to prevent eager loading on initial page load
// import { getApp } from 'firebase/app';
// import { 
//   initializeAppCheck, 
//   ReCaptchaV3Provider,
//   getToken as getAppCheckToken 
// } from 'firebase/app-check';

// Crashlytics is only available in the Firebase SDK for mobile apps
// For web, we'll use a lightweight error tracking approach
let crashlyticsEnabled = false;
let userId: string | null = null;

const STORAGE_KEY = 'crashlytics_enabled';

/**
 * Check if user has opted into crash reporting
 */
export function isCrashlyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

/**
 * Enable or disable crash reporting
 */
export function setCrashlyticsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  
  localStorage.setItem(STORAGE_KEY, String(enabled));
  crashlyticsEnabled = enabled;
  
  if (enabled) {
    initCrashlytics();
  }
}

/**
 * Initialize crash reporting if user has opted in
 */
export async function initCrashlytics(): Promise<void> {
  if (typeof window === 'undefined') return;
  
  crashlyticsEnabled = isCrashlyticsEnabled();
  
  if (!crashlyticsEnabled) {
    return;
  }

  // Set up global error handlers
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

/**
 * Set user ID for crash reports (optional, for debugging)
 */
export function setCrashlyticsUserId(uid: string | null): void {
  userId = uid;
}

/**
 * Log a non-fatal error
 */
export function logError(error: Error, context?: Record<string, any>): void {
  if (!crashlyticsEnabled) return;
  
  const errorData = {
    message: error.message,
    stack: error.stack,
    name: error.name,
    userId: userId || 'anonymous',
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context,
  };

  // Send to your error logging endpoint (Cloud Function)
  sendErrorReport(errorData).catch(() => {
    // Silently fail - don't disrupt UX
  });
}

/**
 * Log a message (breadcrumb)
 */
export function logMessage(message: string): void {
  if (!crashlyticsEnabled) return;
  
  // Store breadcrumbs in memory for context
  if (typeof window !== 'undefined') {
    if (!(window as any).__crashlyticsBreadcrumbs) {
      (window as any).__crashlyticsBreadcrumbs = [];
    }
    (window as any).__crashlyticsBreadcrumbs.push({
      message,
      timestamp: new Date().toISOString(),
    });
    
    // Keep only last 50 breadcrumbs
    if ((window as any).__crashlyticsBreadcrumbs.length > 50) {
      (window as any).__crashlyticsBreadcrumbs.shift();
    }
  }
}

/**
 * Set custom key-value pair for crash context
 */
export function setCustomKey(key: string, value: string | number | boolean): void {
  if (!crashlyticsEnabled) return;
  
  if (typeof window !== 'undefined') {
    if (!(window as any).__crashlyticsCustomKeys) {
      (window as any).__crashlyticsCustomKeys = {};
    }
    (window as any).__crashlyticsCustomKeys[key] = value;
  }
}

/**
 * Handle global uncaught errors
 */
function handleGlobalError(event: ErrorEvent): void {
  if (!crashlyticsEnabled) return;
  
  const error = event.error || new Error(event.message);
  logError(error, {
    type: 'uncaught_error',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
}

/**
 * Handle unhandled promise rejections
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  if (!crashlyticsEnabled) return;
  
  const error = event.reason instanceof Error 
    ? event.reason 
    : new Error(String(event.reason));
    
  logError(error, {
    type: 'unhandled_rejection',
  });
}

/**
 * Send error report to backend
 */
async function sendErrorReport(errorData: any): Promise<void> {
  try {
    const [{ getFunctions, httpsCallable }, { getFirebaseApp }] = await Promise.all([
      import('firebase/functions'),
      import('../lib/firebaseLazy'),
    ]);
    const app = await getFirebaseApp();
    const functions = getFunctions(app, 'us-central1');
    const logCrash = httpsCallable(functions, 'logCrashReport');
    
    await logCrash({
      ...errorData,
      breadcrumbs: (window as any).__crashlyticsBreadcrumbs || [],
      customKeys: (window as any).__crashlyticsCustomKeys || {},
    });
  } catch (error) {
    // Silently fail - don't create infinite error loop
    if (import.meta.env.DEV) {
      console.warn('[Crashlytics] Failed to send error report:', error);
    }
  }
}

/**
 * Clean up crash reporting
 */
export function cleanupCrashlytics(): void {
  window.removeEventListener('error', handleGlobalError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  crashlyticsEnabled = false;
}
